import * as XLSX from 'xlsx';
import Papa from 'papaparse';
import moment from 'moment';
import { PaymentRow, InvoicePaymentRow, PaymentVolumeRow } from './types';

// MM-DD-YYYY stamp for export file names (dashes since "/" is a path separator).
const getExportDateStamp = () => moment().format('MM-DD-YYYY');

// ─── Payments ──────────────────────────────────────────────────────────────────

const FEE_TYPE_DISPLAY_LABELS: Record<string, string> = {
  FISERV_FEE_PAYMENT_BUYER: 'CREDIT_CARD_SURCHARGE',
};

const formatFeeTypeLabel = (value: string): string =>
  value
    .split(',')
    .map((type) => (FEE_TYPE_DISPLAY_LABELS[type] ?? type).replace(/_/g, ' '))
    .join(', ');

const formatPaymentRowForExport = (
  row: PaymentRow,
  columnLabels: Record<keyof PaymentRow, string>,
  visibleKeys?: (keyof PaymentRow)[]
): Record<string, string | number> => {
  const formatted: Record<string, string | number> = {};
  const keys = visibleKeys || (Object.keys(columnLabels) as (keyof PaymentRow)[]);
  keys.forEach((key) => {
    if (key === 'createdAt') {
      formatted[columnLabels[key]] = row[key]
        ? moment(row[key] as string).format('MM/DD/YYYY')
        : '';
    } else if (key === 'paymentMethodType') {
      const val = row[key] as string;
      formatted[columnLabels[key]] = val
        ? val === 'CREDIT_CARD'
          ? 'CARD'
          : val.replace(/_/g, ' ')
        : '';
    } else if (key === 'transactionFeeType') {
      const val = row[key] as string;
      formatted[columnLabels[key]] = row.transactionFee && val ? formatFeeTypeLabel(val) : '';
    } else if (key === 'paymentType') {
      const val = row[key] as string;
      formatted[columnLabels[key]] = val === 'WITHDRAW' ? 'PAYOUT' : val;
    } else {
      formatted[columnLabels[key]] = row[key] as string | number;
    }
  });
  return formatted;
};

export const exportPaymentsToXlsx = (
  rows: PaymentRow[],
  columnLabels: Record<keyof PaymentRow, string>,
  visibleColumns?: (keyof PaymentRow)[]
) => {
  const formattedRows = rows.map((row) =>
    formatPaymentRowForExport(row, columnLabels, visibleColumns)
  );
  const worksheet = XLSX.utils.json_to_sheet(formattedRows);
  const workbook = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(workbook, worksheet, 'Payments');
  XLSX.writeFile(workbook, `payments-${getExportDateStamp()}.xlsx`);
};

export const exportPaymentsToCsv = (
  rows: PaymentRow[],
  columnLabels: Record<keyof PaymentRow, string>,
  visibleColumns?: (keyof PaymentRow)[]
) => {
  const formattedRows = rows.map((row) =>
    formatPaymentRowForExport(row, columnLabels, visibleColumns)
  );
  const csv = Papa.unparse(formattedRows);
  const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
  const link = document.createElement('a');
  const url = URL.createObjectURL(blob);
  link.setAttribute('href', url);
  link.setAttribute('download', `payments-${getExportDateStamp()}.csv`);
  link.style.visibility = 'hidden';
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
};

// ─── Invoice Payments ──────────────────────────────────────────────────────────

const formatInvoicePaymentRowForExport = (
  row: InvoicePaymentRow,
  columnLabels: Record<keyof InvoicePaymentRow, string>,
  visibleKeys?: (keyof InvoicePaymentRow)[]
): Record<string, string | number> => {
  const formatted: Record<string, string | number> = {};
  const keys = visibleKeys || (Object.keys(columnLabels) as (keyof InvoicePaymentRow)[]);
  keys.forEach((key) => {
    if (key === 'createdAt') {
      formatted[columnLabels[key]] = row[key]
        ? moment(row[key] as string).format('MM/DD/YYYY')
        : '';
    } else {
      formatted[columnLabels[key]] = row[key] as string | number;
    }
  });
  return formatted;
};

export const exportInvoicePaymentsToXlsx = (
  rows: InvoicePaymentRow[],
  columnLabels: Record<keyof InvoicePaymentRow, string>,
  visibleColumns?: (keyof InvoicePaymentRow)[]
) => {
  const formattedRows = rows.map((row) =>
    formatInvoicePaymentRowForExport(row, columnLabels, visibleColumns)
  );
  const worksheet = XLSX.utils.json_to_sheet(formattedRows);
  const workbook = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(workbook, worksheet, 'Invoice Payments');
  XLSX.writeFile(workbook, `invoice-payments-${getExportDateStamp()}.xlsx`);
};

export const exportInvoicePaymentsToCsv = (
  rows: InvoicePaymentRow[],
  columnLabels: Record<keyof InvoicePaymentRow, string>,
  visibleColumns?: (keyof InvoicePaymentRow)[]
) => {
  const formattedRows = rows.map((row) =>
    formatInvoicePaymentRowForExport(row, columnLabels, visibleColumns)
  );
  const csv = Papa.unparse(formattedRows);
  const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
  const link = document.createElement('a');
  const url = URL.createObjectURL(blob);
  link.setAttribute('href', url);
  link.setAttribute('download', `invoice-payments-${getExportDateStamp()}.csv`);
  link.style.visibility = 'hidden';
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
};

// ─── Payment Volume by Month ───────────────────────────────────────────────────

const PAYMENT_VOLUME_COLUMN_LABELS: Record<keyof PaymentVolumeRow, string> = {
  id: 'ID',
  monthLabel: 'Month/Year',
  month: 'Month',
  year: 'Year',
  totalVolume: 'Total Volume',
  cardVolume: 'Card Volume',
  bankVolume: 'Bank ACH Volume',
};

const PAYMENT_VOLUME_EXPORT_KEYS: (keyof PaymentVolumeRow)[] = [
  'monthLabel',
  'totalVolume',
  'cardVolume',
  'bankVolume',
];

const formatPaymentVolumeRowForExport = (
  row: PaymentVolumeRow
): Record<string, string | number> => {
  const formatted: Record<string, string | number> = {};
  PAYMENT_VOLUME_EXPORT_KEYS.forEach((key) => {
    formatted[PAYMENT_VOLUME_COLUMN_LABELS[key]] = row[key];
  });
  return formatted;
};

export const exportPaymentVolumeToXlsx = (rows: PaymentVolumeRow[]) => {
  const formattedRows = rows.map(formatPaymentVolumeRowForExport);
  const worksheet = XLSX.utils.json_to_sheet(formattedRows);
  const workbook = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(workbook, worksheet, 'Payment Volume');
  XLSX.writeFile(workbook, `payment-volume-${getExportDateStamp()}.xlsx`);
};

export const exportPaymentVolumeToCsv = (rows: PaymentVolumeRow[]) => {
  const formattedRows = rows.map(formatPaymentVolumeRowForExport);
  const csv = Papa.unparse(formattedRows);
  const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
  const link = document.createElement('a');
  const url = URL.createObjectURL(blob);
  link.setAttribute('href', url);
  link.setAttribute('download', `payment-volume-${getExportDateStamp()}.csv`);
  link.style.visibility = 'hidden';
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
};
